import { Container, type ContainerOptions } from "../core/Container.js";
import type { Control } from "../core/Control.js";
import type { Desktop, DesktopWindow } from "../core/Desktop.js";
import type { KeyEventArgs, MouseEventArgs } from "../core/events.js";
import {
  clamp,
  fitRectInto,
  nudgeRectIntoReach,
  slideRectInto,
  rectContains,
  type Point,
  type Rect,
} from "../../common/geometry.js";
import type { RenderContext, TooltipContent } from "../render/RenderContext.js";
import { createGlyph, type GlyphPainter } from "../themes/glyphRegistry.js";
import { snapRect, snapZoneAt, type SnapZone } from "../core/arrange.js";
import { showMenu } from "./ContextMenu.js";
import { menuSeparator, type MenuItemSpec } from "./MenuItem.js";
import type {
  CaptionButton,
  Theme,
  WindowBorderStyle,
  WindowVisualState,
} from "../themes/Theme.js";

export type WindowPart =
  | "client"
  | "title"
  /** One of the window's own caption actions; `captionActionAt` says which. */
  | "action"
  | "minimize"
  | "maximize"
  | "close"
  | "n"
  | "s"
  | "e"
  | "w"
  | "ne"
  | "nw"
  | "se"
  | "sw";

/** How much of a dragged window must stay on screen under `reachable`. */
const DRAG_MARGIN = 24;

/** How long the frame flashes when a modal refuses a click. */
const FLASH_MS = 320;

const RESIZE_CURSORS: Record<string, string> = {
  n: "ns-resize",
  s: "ns-resize",
  e: "ew-resize",
  w: "ew-resize",
  ne: "nesw-resize",
  sw: "nesw-resize",
  nw: "nwse-resize",
  se: "nwse-resize",
};

export interface WindowOptions extends ContainerOptions {
  title?: string;
  /**
   * How much frame the window wears. `sizable` (the default) is the full
   * theme frame; `single` is a fixed-size window with a caption; `none` is
   * chrome-free. See `WindowBorderStyle`.
   */
  borderStyle?: WindowBorderStyle;
  resizable?: boolean;
  showMinimize?: boolean;
  showMaximize?: boolean;
  showClose?: boolean;
  /** Fit the window around its content as soon as it has a theme to measure with. */
  sizeToContent?: boolean;
  minWidth?: number;
  minHeight?: number;
  /** The window this one belongs to. See `Window.owner`. */
  owner?: Window | null;
  /** A shorter caption with a close button only. See `Window.toolWindow`. */
  toolWindow?: boolean;
  /** Buttons of the application's own, drawn in the caption left of the control box. */
  captionActions?: CaptionAction[];
}

/**
 * A button an application adds to a window's caption, beside minimize, maximize and close.
 *
 * The window draws and hit-tests it the way it does its own three, in the theme's caption button
 * width and ink, so every theme gets it without knowing about it: a theme owns how a caption
 * looks, and an application owns what else it asks of one. The glyph is a painter or the name of
 * a registered glyph. Not shown on a parked stub, which has room for its title and nothing else.
 */
export interface CaptionAction {
  glyph: GlyphPainter | string;
  tooltip?: TooltipContent;
  onClick: (window: Window) => void;
}

interface DragState {
  kind: "move" | "resize";
  part: WindowPart;
  /** Pointer position at drag start, canvas coords. */
  origin: Point;
  startBounds: Rect;
  /** A maximized or snapped window comes back to its own size once the pointer really moves. */
  unsnap: boolean;
  /** The snap the pointer is over, applied on release. */
  zone: SnapZone | null;
}

/** How far one arrow key moves or sizes a window from the system menu; Ctrl moves one unit. */
const KEY_TRACK_STEP = 8;

/** How far a maximized or snapped window has to be dragged before it lets go of its place. */
const UNSNAP_DISTANCE = 4;

/**
 * A movable, resizable, z-ordered top-level window.
 *
 * The window paints and hit-tests its own non-client area (title bar, caption
 * buttons, resize grips) from theme metrics, so switching themes changes the
 * chrome — and the geometry that goes with it — without touching this class.
 */
export class Window extends Container implements DesktopWindow {
  /** Marks this child of the desktop as a real window. See isDesktopWindow. */
  readonly isWindow = true as const;

  #title: string;
  #active = false;
  #maximized = false;
  #minimized = false;
  #restoreBounds: Rect | null = null;
  #drag: DragState | null = null;
  #hoveredButton: CaptionButton | null = null;
  #pressedButton: CaptionButton | null = null;
  #captionActions: CaptionAction[] = [];
  #hoveredAction: number | null = null;
  #pressedAction: number | null = null;
  #flashUntil = 0;
  #flashTimer = 0;
  /**
   * A minimize or a restore in flight: one rectangle on its way to another.
   * A null `to` means "wherever the window's bounds are when we get there",
   * so a restore still lands correctly if something moves the window — a
   * maximize, say — while it is on its way back out.
   */
  #flight: { from: Rect; to: Rect | null; start: number; ms: number } | null = null;
  /** A setClientSize() made before a theme was available. */
  #pendingClientSize: { width: number; height: number } | null = null;
  /** Asked to fit its content before there was a theme to measure with. */
  #sizeToContent = false;

  #borderStyle: WindowBorderStyle;
  #resizable: boolean;
  #showMinimize: boolean;
  #showMaximize: boolean;
  #showClose: boolean;
  minWidth: number;
  minHeight: number;
  #owner: Window | null = null;
  #toolWindow: boolean;
  /** A system-menu Move or Size in progress from the keyboard, and where it began. */
  #keyTrack: { kind: "move" | "size"; start: Rect } | null = null;
  /** The size a snapped window had before it was snapped, to go back to when dragged away. */
  #snapRestore: { width: number; height: number } | null = null;
  /** Owned windows this one hid when it was minimized, to show again when it is restored. */
  #hiddenOwned: Window[] = [];

  /** Overridden by Dialog. */
  get modal(): boolean {
    return false;
  }

  constructor(options: WindowOptions = {}) {
    super({ width: 480, height: 320, ...options });
    this.#title = options.title ?? "";
    this.#borderStyle = options.borderStyle ?? "sizable";
    this.#resizable = options.resizable ?? true;
    this.#showMinimize = options.showMinimize ?? true;
    this.#showMaximize = options.showMaximize ?? true;
    this.#showClose = options.showClose ?? true;
    this.#sizeToContent = options.sizeToContent ?? false;
    this.#captionActions = [...(options.captionActions ?? [])];
    this.minWidth = options.minWidth ?? 160;
    this.minHeight = options.minHeight ?? 80;
    this.#toolWindow = options.toolWindow ?? false;
    if (options.owner) this.owner = options.owner;

    this.on("pointerdown", (e) => this.#onPointerDown(e));
    this.on("pointermove", (e) => this.#onPointerMove(e));
    this.on("pointerup", (e) => this.#onPointerUp(e));
    this.on("pointerleave", () => {
      if (this.#hoveredButton || this.#hoveredAction !== null) {
        this.#hoveredButton = null;
        this.#hoveredAction = null;
        this.invalidate();
      }
    });
    // A parked window that is hidden gives up its slot in the strip, and
    // takes one again when it is shown: the desktop cannot see that coming.
    // A window hidden mid-flight stops flying — nothing is painting it, so
    // nothing would ever notice that it had arrived.
    this.on("visiblechanged", () => {
      if (!this.visible) this.#endFlight();
      if (this.#minimized) this.desktop?.invalidateMinimizedLayout();
    });
  }

  get title(): string {
    return this.#title;
  }
  set title(v: string) {
    if (this.#title === v) return;
    this.#title = v;
    this.invalidate();
    // A taskbar shows the title, so it has to hear about this.
    this.emit("titlechanged");
  }

  get active(): boolean {
    return this.#active;
  }

  get maximized(): boolean {
    return this.#maximized;
  }

  /**
   * True while the window is parked.
   *
   * Distinct from `visible`, which stays exactly as the application set it:
   * minimizing is the user putting a window aside, hiding is the program
   * taking it away, and a window can be both. Only this one is undone by
   * `restore()`.
   */
  get minimized(): boolean {
    return this.#minimized;
  }

  /** The theme in force, or null while the window is detached. */
  protected get themeOrNull(): Theme | null {
    return this.desktop?.theme ?? null;
  }

  // --- chrome ------------------------------------------------------------

  /**
   * How much frame the window wears — `sizable`, `single` or `none`.
   *
   * Changing it changes how much of the window is client area, so everything
   * inside has to be laid out again against the new rectangle.
   */
  /** The application's own caption buttons, left of the control box. Replace the array to change them. */
  get captionActions(): readonly CaptionAction[] {
    return this.#captionActions;
  }
  set captionActions(v: readonly CaptionAction[]) {
    this.#captionActions = [...v];
    this.#hoveredAction = null;
    this.#pressedAction = null;
    this.invalidate();
  }

  /**
   * Where caption action `index` is drawn, in the window's own coordinates, or null when the
   * window has no caption, is parked, or has no such action. Actions stack leftwards from the
   * leftmost standard button, each as wide as the theme's caption buttons.
   */
  captionActionRect(index: number): Rect | null {
    const theme = this.themeOrNull;
    if (!theme || index < 0 || index >= this.#captionActions.length) return null;
    const state = this.windowState();
    if (state.minimized || state.borderStyle === "none") return null;
    const drawn = this.presentationRect;
    const border = theme.frameBorder(state);
    let left = drawn.width - border;
    for (const button of ["minimize", "maximize", "close"] as CaptionButton[]) {
      const r = theme.captionButtonRect(drawn, button, state);
      if (r) left = Math.min(left, r.x);
    }
    const w = theme.metrics.captionButtonWidth;
    return { x: left - (index + 1) * w, y: border, width: w, height: theme.captionHeight(state) };
  }

  /** The caption action under a window-local point, or null. */
  captionActionAt(p: Point): number | null {
    for (let i = 0; i < this.#captionActions.length; i++) {
      const r = this.captionActionRect(i);
      if (r && rectContains(r, p)) return i;
    }
    return null;
  }

  override getTooltip(p: Point): TooltipContent | null {
    const index = this.captionActionAt(p);
    if (index !== null) return this.#captionActions[index]?.tooltip ?? null;
    return super.getTooltip(p);
  }

  get borderStyle(): WindowBorderStyle {
    return this.#borderStyle;
  }
  set borderStyle(v: WindowBorderStyle) {
    if (this.#borderStyle === v) return;
    this.#borderStyle = v;
    // A drag in flight was started against chrome that may no longer be
    // there; a half-finished resize of a now-fixed window is not wanted.
    this.#endDrag();
    this.#captionButtonsChanged();
    // The client rectangle just moved — as much as a theme swap moves it — so
    // anchors, docking and any subclass layout all have to run against it.
    this.layout();
  }

  /**
   * Whether the window's edges may be dragged, if its border style allows it.
   *
   * A request, like the `show*` flags: `single` and `none` are fixed sizes,
   * and a maximized window is already the work area. `canResize` is the
   * reconciled answer, and the one the rest of the class asks.
   */
  get resizable(): boolean {
    return this.#resizable;
  }
  set resizable(v: boolean) {
    if (this.#resizable === v) return;
    this.#resizable = v;
    if (!v) this.#endDrag();
    this.invalidate();
  }

  /**
   * Whether the minimize button is in the control box.
   *
   * The three `show*` flags are requests, not facts: a borderless window has
   * no caption to put a button in, so it shows none of them whatever these
   * say. `canMinimize` and friends are the reconciled answers.
   */
  get showMinimize(): boolean {
    return this.#showMinimize;
  }
  set showMinimize(v: boolean) {
    if (this.#showMinimize === v) return;
    this.#showMinimize = v;
    this.#captionButtonsChanged();
  }

  get showMaximize(): boolean {
    return this.#showMaximize;
  }
  set showMaximize(v: boolean) {
    if (this.#showMaximize === v) return;
    this.#showMaximize = v;
    this.#captionButtonsChanged();
  }

  get showClose(): boolean {
    return this.#showClose;
  }
  set showClose(v: boolean) {
    if (this.#showClose === v) return;
    this.#showClose = v;
    this.#captionButtonsChanged();
  }

  /**
   * Hiding one button slides the rest along, so a button the pointer was over
   * — or holding down — may not be there any more.
   */
  #captionButtonsChanged(): void {
    if (this.#hoveredButton && !this.#buttonVisible(this.#hoveredButton)) this.#hoveredButton = null;
    if (this.#pressedButton && !this.#buttonVisible(this.#pressedButton)) {
      this.#pressedButton = null;
      this.desktop?.input.releaseCapture(this);
    }
    this.invalidate();
  }

  #buttonVisible(button: CaptionButton): boolean {
    if (button === "minimize") return this.canMinimize;
    if (button === "maximize") return this.canMaximize;
    return this.canClose;
  }

  /** Whether the window has a caption bar at all. */
  get hasCaption(): boolean {
    return this.#borderStyle !== "none";
  }

  /**
   * Whether the user may resize the window right now.
   *
   * A maximized window is already the size of the work area, and a `single`
   * or `none` one is fixed by its border style — so both say no however
   * `resizable` was set.
   */
  get canResize(): boolean {
    return this.#resizable && this.#borderStyle === "sizable" && !this.#maximized;
  }

  /**
   * Whether the maximize button shows and double-clicking the caption works.
   *
   * `single` means fixed size, and maximizing is the one thing that would
   * undo that, so it goes with the resize grips. `maximize()` itself still
   * works if you call it — an explicit request does what it says.
   */
  /**
   * WinForms' tool-window border styles, as a flag that goes with `sizable` or `single`: a
   * shorter caption, a close button and nothing else, so it cannot be minimized or maximized.
   * Usually given an `owner`, which keeps it above the window it serves.
   */
  get toolWindow(): boolean {
    return this.#toolWindow;
  }
  set toolWindow(v: boolean) {
    if (this.#toolWindow === v) return;
    this.#toolWindow = v;
    if (this.#maximized && v) this.restore();
    this.#captionButtonsChanged();
    // The caption changes height, so the client area does too.
    this.layout();
  }

  get canMaximize(): boolean {
    return this.#showMaximize && !this.#toolWindow && this.#borderStyle === "sizable";
  }

  get canMinimize(): boolean {
    return this.#showMinimize && !this.#toolWindow && this.hasCaption;
  }

  get canClose(): boolean {
    return this.#showClose && this.hasCaption;
  }

  /**
   * How the window looks right now, for the theme to paint and measure.
   *
   * While the window is parked this describes the stub rather than the
   * window: no grips, no minimize button (it is already minimized) and no
   * maximize button, because a stub the size of a caption has no room to say
   * anything but "here I am" and "go away".
   */
  windowState(): WindowVisualState {
    return this.#stateFor(this.#minimized || this.minimizing);
  }

  #stateFor(stub: boolean): WindowVisualState {
    return {
      hovered: this.hovered,
      pressed: this.pressed,
      focused: this.#active,
      disabled: !this.enabled,
      active: this.#active,
      // A parked window is a caption bar and nothing else, so it wears one
      // even when it normally has no chrome at all — a borderless window
      // still has to be findable and clickable once it is parked.
      borderStyle: stub && this.#borderStyle === "none" ? "single" : this.#borderStyle,
      resizable: !stub && this.canResize,
      showMinimize: !stub && this.canMinimize,
      showMaximize: !stub && this.canMaximize,
      showClose: stub ? this.#showClose : this.canClose,
      maximized: !stub && this.#maximized,
      toolWindow: this.#toolWindow,
      minimized: stub,
      hoveredButton: this.#hoveredButton,
      pressedButton: this.#pressedButton,
      flash: this.flash,
    };
  }

  // --- where the window is drawn ------------------------------------------

  /**
   * The rectangle the window is drawn and clicked in, in desktop
   * coordinates.
   *
   * Its own `bounds`, except while it is parked, when it is the stub the
   * desktop tiled into the corner of the work area. The two are kept apart
   * on purpose: squeezing a window down to a caption bar by setting its
   * bounds would run every anchor and every dock inside it against a
   * rectangle a few pixels tall, and growing it back would not undo that.
   * Nothing about a minimized window's layout moves at all.
   */
  get presentationRect(): Rect {
    return this.#flightRect() ?? this.#parkedRect() ?? this.bounds;
  }

  /** Where the desktop parked this window, or null if it is not parked. */
  #parkedRect(): Rect | null {
    if (!this.#minimized) return null;
    return this.desktop?.parkedRect(this) ?? null;
  }

  /** True while the window is flying to or from its minimized place. */
  get minimizing(): boolean {
    return this.#flightRect() !== null;
  }

  /**
   * Where the flight has got to, or null when there is not one.
   *
   * Answers from the clock rather than from a frame counter, and answers
   * null the moment the time is up rather than waiting to be told the
   * animation is over: the geometry is then right whether or not anything
   * has painted since. Clearing the state is `#stepFlight`'s job.
   */
  #flightRect(): Rect | null {
    const flight = this.#flight;
    if (!flight) return null;
    const to = flight.to ?? this.bounds;
    const t = Math.max(0, (performance.now() - flight.start) / flight.ms);
    if (t >= 1) return null;
    // Ease out: most of the distance early, so the eye follows the window to
    // where it is going rather than watching it accelerate away.
    const e = 1 - Math.pow(1 - t, 3);
    const at = (a: number, b: number): number => a + (b - a) * e;
    return {
      x: at(flight.from.x, to.x),
      y: at(flight.from.y, to.y),
      width: at(flight.from.width, to.width),
      height: at(flight.from.height, to.height),
    };
  }

  /**
   * Fly the window from one rectangle to another.
   *
   * Only the frame makes the trip — the caption, the border and the title,
   * drawn by the same theme calls at every size along the way, so the shape
   * that arrives is exactly the stub (or the window) that will be sitting
   * there when it stops. The contents are not drawn while it is moving:
   * there is nothing to read in a window sliding past at speed, and laying
   * them out for each frame of it would be work spent on a blur.
   */
  #beginFlight(from: Rect | null, to: Rect | null): void {
    this.#endFlight();
    const desktop = this.desktop;
    if (!desktop || !from) return;
    const ms = desktop.minimizeAnimationMs;
    if (!(ms > 0) || !this.effectivelyVisible) return;
    if (from.width <= 0 || from.height <= 0) return;
    if (to && (to.width <= 0 || to.height <= 0)) return;
    this.#flight = { from, to, start: performance.now(), ms };
    desktop.addAnimator(this);
    this.invalidate();
  }

  #endFlight(): void {
    if (!this.#flight) return;
    this.#flight = null;
    this.desktop?.removeAnimator(this);
    this.invalidate();
  }

  /** Called from paint: a flight whose time is up stops being one. */
  #stepFlight(): void {
    const flight = this.#flight;
    if (flight && performance.now() - flight.start >= flight.ms) this.#endFlight();
  }

  /**
   * How tall this window's stub is: its frame plus one caption bar.
   *
   * Asked by the desktop when it tiles the strip. Zero while the window is
   * detached, which is the desktop's cue to skip it.
   */
  minimizedHeight(): number {
    const theme = this.themeOrNull;
    if (!theme) return 0;
    const state = this.#stateFor(true);
    return theme.frameBorder(state) * 2 + theme.captionHeight(state);
  }

  /**
   * Parked windows are drawn away from their own bounds, so the mapping
   * between this window's space and the desktop's has to follow them there.
   * Everything built on it — pointer coordinates, `fromScreen`, a tooltip's
   * position — then lands on the stub without knowing there is one.
   */
  override toParentSpace(p: Point): Point {
    const r = this.presentationRect;
    const q = { x: p.x + r.x, y: p.y + r.y };
    return this.parent ? this.parent.childToLocal(q) : q;
  }

  override fromParentSpace(p: Point): Point {
    const r = this.presentationRect;
    const q = this.parent ? this.parent.localToChild(p) : p;
    return { x: q.x - r.x, y: q.y - r.y };
  }

  /** The rectangle on the canvas — the stub's, while the window is parked. */
  override get screenBounds(): Rect {
    const r = this.presentationRect;
    const tl = this.toScreen({ x: 0, y: 0 });
    const br = this.toScreen({ x: r.width, y: r.height });
    return { x: tl.x, y: tl.y, width: br.x - tl.x, height: br.y - tl.y };
  }

  // --- geometry ---------------------------------------------------------

  override get clientRect(): Rect {
    const theme = this.themeOrNull;
    // A borderless window is all client area by definition, so no theme is
    // consulted — which is also why themes never see that case.
    if (!theme || this.#borderStyle === "none") return this.localRect;
    // Deliberately the un-parked state: what the children are laid out into
    // must not depend on whether the window happens to be minimized, or
    // parking one would re-flow everything inside it and back again.
    return theme.windowClientRect(this.bounds, this.#stateFor(false));
  }

  /**
   * Title bar rectangle, in the space the window is drawn in — which is the
   * stub's own space while it is parked. Empty when borderless.
   */
  get titleBarRect(): Rect {
    const theme = this.themeOrNull;
    const drawn = this.presentationRect;
    if (!theme) return { x: 0, y: 0, width: drawn.width, height: 0 };
    const state = this.windowState();
    const b = theme.frameBorder(state);
    return {
      x: b,
      y: b,
      width: Math.max(0, drawn.width - b * 2),
      height: theme.captionHeight(state),
    };
  }

  /**
   * Resize the window so its client area is exactly this size.
   *
   * Before the window is attached there is no theme, and so no way to know how
   * much space the frame takes. The request is remembered and applied as soon
   * as one is available.
   */
  setClientSize(width: number, height: number): void {
    const theme = this.themeOrNull;
    if (!theme) {
      this.#pendingClientSize = { width, height };
      this.setSize(width, height);
      return;
    }
    this.#pendingClientSize = null;
    const client = this.clientRect;
    this.setSize(this.width - client.width + width, this.height - client.height + height);
  }

  /**
   * Resize so the client area is exactly what the children need.
   *
   * Measured the way `performDock` lays out — bars along their edges, whatever fills the middle
   * asking for its own preferred size — so a window built out of docked chrome and one measured
   * panel comes out the size of its content with nothing to compute by hand.
   */
  sizeToContent(): void {
    if (!this.themeOrNull) {
      this.#sizeToContent = true;
      return;
    }
    const content = this.measureContent(this.clientSize);
    this.setClientSize(
      Math.max(this.minWidth, content.width),
      Math.max(this.minHeight, content.height),
    );
  }

  protected override onEnvironmentChanged(): void {
    const pending = this.#pendingClientSize;
    if (pending) this.setClientSize(pending.width, pending.height);
    // Measuring needs a theme for the frame and for the fonts inside, so a window asked to fit
    // its content before it was shown fits it here, the first moment the answer exists.
    if (this.#sizeToContent) {
      this.#sizeToContent = false;
      this.sizeToContent();
    }
  }

  /**
   * A busy window keeps its caption clear and live. A window you cannot move
   * or close is a trap, and a hung operation is exactly when the user wants
   * to get out — so the scrim stops at the client area and the title bar,
   * caption buttons and resize grips go on working.
   */
  protected override get busyRect(): Rect {
    return this.clientRect;
  }

  // --- activation and lifecycle ------------------------------------------

  setActive(active: boolean): void {
    if (this.#active === active) return;
    this.#active = active;
    if (!active) this.#keyTrack = null;
    this.emit(active ? "activated" : "deactivated");
    this.invalidate();
  }

  activate(): void {
    this.desktop?.activate(this);
  }

  /**
   * The window this one belongs to, as a Win32 owner: an owned window is always stacked above
   * its owner, is hidden while the owner is minimized and comes back with it, and is closed
   * when the owner closes. A tool palette, a find box, a dialog asked from one window.
   */
  get owner(): Window | null {
    return this.#owner;
  }
  set owner(v: Window | null) {
    for (let w: Window | null = v; w; w = w.#owner) {
      if (w === this) throw new Error("A window cannot own itself, directly or through another.");
    }
    this.#owner = v;
    // Re-raising the active owner restacks its owned windows above it.
    const desktop = this.desktop;
    if (v && desktop && desktop.activeWindow === v) desktop.activate(v);
  }

  /** Windows on the same desktop that this one owns, back to front. */
  get ownedWindows(): Window[] {
    const desktop = this.desktop;
    if (!desktop) return [];
    return desktop.windows.filter((w): w is Window => w instanceof Window && w.#owner === this);
  }

  /** Add this window to a desktop and bring it to the front. */
  show(desktop: Desktop): this {
    desktop.addWindow(this);
    return this;
  }

  /** Raise `closing` (cancelable) then `closed`, and detach from the desktop. */
  close(): boolean {
    const args = { cancel: false };
    this.emit("closing", args);
    if (args.cancel) return false;
    // As WinForms does it: an owned window that refuses to close keeps its owner open too.
    for (const owned of this.ownedWindows.reverse()) if (!owned.close()) return false;
    this.desktop?.removeWindow(this);
    this.emit("closed");
    return true;
  }

  /**
   * Park the window: stop drawing it where it is, and put it somewhere it
   * can be found again.
   *
   * Where that is depends on who is listening. A taskbar claims its windows
   * through `Desktop.minimizeTarget` and shows them as buttons; when nothing
   * does, the desktop tiles a stub — a caption bar with the title on it —
   * along the bottom of the work area, so minimizing is never a way to lose
   * a window. Either way the window itself is untouched: same bounds, same
   * layout, same `visible`, still running.
   */
  minimize(): void {
    if (this.#minimized) return;
    const from = this.#flightRect() ?? this.bounds;
    this.#minimized = true;
    const desktop = this.desktop;
    this.#hiddenOwned = this.ownedWindows.filter((w) => w.visible);
    for (const owned of this.#hiddenOwned) owned.visible = false;
    // A caret blinking in a window nobody can see is a keystroke going
    // somewhere invisible — the same reason hiding a control drops focus.
    desktop?.focusManager.clearWithin(this);
    this.emit("minimized");
    this.invalidate();
    if (!desktop) return;
    desktop.notifyMinimized(this, true);
    // From wherever it is now — which is mid-air if a restore was still
    // running — to wherever it has just been told to go.
    this.#beginFlight(from, desktop.minimizedRect(this));
    if (desktop.activeWindow === this) {
      const next = desktop.windows.filter((w) => w !== this && w.visible && !w.minimized).pop();
      if (next) desktop.activate(next);
    }
  }

  /**
   * Undo one step — un-park a minimized window, or un-maximize a maximized
   * one — and bring it forward.
   *
   * One step, not both: a window minimized while maximized comes back
   * maximized, the way restoring one from a taskbar does. Restore it twice
   * to get all the way back to its old bounds.
   */
  restore(): void {
    if (this.#minimized) {
      const desktop = this.desktop;
      // Where it is coming back from: mid-air if it was still on its way in,
      // otherwise the stub or the taskbar button it has been sitting in.
      const from = this.#flightRect() ?? desktop?.minimizedRect(this) ?? null;
      this.#minimized = false;
      this.emit("restored");
      this.invalidate();
      desktop?.notifyMinimized(this, false);
      for (const owned of this.#hiddenOwned) if (owned.desktop === desktop) owned.visible = true;
      this.#hiddenOwned = [];
      // A null destination: back to the window's own bounds, read when it
      // arrives, so maximizing it on the way out still lands full-screen.
      this.#beginFlight(from, null);
      this.activate();
      // `activate` declines to put focus inside a parked window, and this one
      // was still parked when a click on its stub activated it.
      if (desktop?.activeWindow === this) desktop.focusManager.restoreIn(this);
      return;
    }
    if (this.#maximized && this.#restoreBounds) {
      this.bounds = this.#restoreBounds;
      this.#restoreBounds = null;
      this.#maximized = false;
      this.desktop?.emit("windowschanged");
    }
    this.activate();
  }

  maximize(): void {
    const desktop = this.desktop;
    if (!desktop || this.#maximized) return;
    // Maximizing a parked window brings it back first: it is going to end up
    // filling the work area either way, and the stub has to be unwound.
    if (this.#minimized) this.restore();
    this.#restoreBounds = this.bounds;
    this.#maximized = true;
    this.applyMaximizedBounds();
    desktop.emit("windowschanged");
  }

  /**
   * Shrink and move so the whole window sits inside the work area.
   *
   * Called when a window joins the desktop, so nothing is ever born
   * off-screen — which is what a layout designed for a laptop does on a
   * phone. Honors the minimums until they no longer fit, then the work area
   * wins: a window too small to use beats one that cannot be reached.
   */
  fitIntoWorkArea(): void {
    const desktop = this.desktop;
    if (!desktop) return;
    if (this.#maximized) {
      this.applyMaximizedBounds();
      return;
    }
    const fitted = fitRectInto(this.bounds, desktop.workArea, {
      width: this.minWidth,
      height: this.minHeight,
    });
    this.setBounds(fitted.x, fitted.y, fitted.width, fitted.height);
  }

  /**
   * Re-apply the desktop's `windowClamp` to this window.
   *
   * `viewport` puts the whole window back inside the work area; `reachable`
   * only pulls it far enough to be grabbed again, so one the user deliberately
   * pushed half off the edge stays where they put it.
   */
  applyClamp(): void {
    const desktop = this.desktop;
    if (!desktop || desktop.windowClamp === "off") return;
    if (desktop.windowClamp === "viewport") this.fitIntoWorkArea();
    else this.keepReachable();
  }

  /**
   * Pull the window back far enough to be grabbed again.
   *
   * Used when the desktop itself resizes — a browser window dragged narrower,
   * a phone rotated. Deliberately weaker than `fitIntoWorkArea`: a window the
   * user deliberately pushed half off the edge stays where they put it, as
   * long as its title bar is still there to grab.
   */
  keepReachable(): void {
    const desktop = this.desktop;
    if (!desktop) return;
    if (this.#maximized) {
      this.applyMaximizedBounds();
      return;
    }
    const at = nudgeRectIntoReach(
      this.bounds,
      desktop.workArea,
      Math.max(this.minWidth / 2, 80),
      // A borderless window has no caption, so there is no grab handle to
      // keep on screen — leave the same margin a drag would.
      Math.max(this.titleBarRect.height, DRAG_MARGIN),
    );
    if (at.x !== this.x || at.y !== this.y) this.setLocation(Math.round(at.x), Math.round(at.y));
  }

  /** Re-fit a maximized window; also called when the work area changes. */
  applyMaximizedBounds(): void {
    const desktop = this.desktop;
    if (!desktop || !this.#maximized) return;
    const area = desktop.workArea;
    this.setBounds(area.x, area.y, area.width, area.height);
  }

  toggleMaximize(): void {
    if (this.#maximized) this.restore();
    else this.maximize();
  }

  /** Modal rejection feedback: briefly redraw the title bar highlighted. */
  flashBlocked(): void {
    this.#flashUntil = performance.now() + FLASH_MS;
    const desktop = this.desktop;
    if (!desktop) return;
    desktop.addAnimator(this);
    // Clicking again restarts the flash, so the old timer must not stop the
    // animation part way through the new one.
    if (this.#flashTimer !== 0) clearTimeout(this.#flashTimer);
    this.#flashTimer = setTimeout(() => {
      this.#flashTimer = 0;
      desktop.removeAnimator(this);
      desktop.invalidate();
    }, FLASH_MS + 20) as unknown as number;
  }

  /** How much flash is left to draw: 1 just after a refused click, then 0. */
  get flash(): number {
    const left = this.#flashUntil - performance.now();
    return left <= 0 ? 0 : Math.min(1, left / FLASH_MS);
  }

  // --- non-client hit testing --------------------------------------------

  /**
   * A parked window is the stub and nothing else: `p` is already in the
   * stub's space (see `fromParentSpace`), what is inside the window is not
   * reachable, and the stub is where the pointer finds it.
   */
  override hitTest(p: Point): Control | null {
    // A window in flight is not a target: it is somewhere else by the time
    // the click lands, and a press that hit the middle of a shrinking frame
    // would mean nothing.
    if (this.#flightRect()) return null;
    if (!this.#minimized) return super.hitTest(p);
    if (!this.visible) return null;
    const stub = this.#parkedRect();
    if (!stub) return null;
    return rectContains({ x: 0, y: 0, width: stub.width, height: stub.height }, p) ? this : null;
  }

  /** Which piece of chrome (or the client area) is at a window-local point. */
  partAt(p: Point): WindowPart {
    const theme = this.themeOrNull;
    if (!theme) return "client";
    const state = this.windowState();
    // The rect the window is actually wearing — its own, or its stub's, and
    // `p` arrives in that same space either way.
    const drawn = this.presentationRect;

    for (const button of ["close", "maximize", "minimize"] as CaptionButton[]) {
      const r = theme.captionButtonRect(drawn, button, state);
      if (r && rectContains(r, p)) return button;
    }
    if (this.captionActionAt(p) !== null) return "action";

    if (state.resizable) {
      const g = Math.max(theme.metrics.resizeGripSize, theme.metrics.windowBorder);
      const west = p.x < g;
      const east = p.x >= drawn.width - g;
      const north = p.y < g;
      const south = p.y >= drawn.height - g;
      if (north && west) return "nw";
      if (north && east) return "ne";
      if (south && west) return "sw";
      if (south && east) return "se";
      if (north) return "n";
      if (south) return "s";
      if (west) return "w";
      if (east) return "e";
    }

    if (rectContains(this.titleBarRect, p)) return "title";
    return "client";
  }

  override cursorAt(p: Point): string {
    if (this.#drag?.kind === "resize") return RESIZE_CURSORS[this.#drag.part] ?? "default";
    // Falls through to Container, which shows the wait cursor over a busy
    // client area but leaves the caption alone.
    return RESIZE_CURSORS[this.partAt(p)] ?? super.cursorAt(p);
  }

  // --- dragging -----------------------------------------------------------

  #onPointerDown(e: MouseEventArgs): void {
    // A click ends a keyboard Move or Size where it got to, as it does on Windows.
    this.#keyTrack = null;
    const part = this.partAt({ x: e.x, y: e.y });
    if (part === "action") {
      this.#pressedAction = this.captionActionAt({ x: e.x, y: e.y });
      this.desktop?.input.setCapture(this);
      this.invalidate();
      e.handled = true;
      return;
    }
    if (part === "minimize" || part === "maximize" || part === "close") {
      this.#pressedButton = part;
      this.desktop?.input.setCapture(this);
      this.invalidate();
      e.handled = true;
      return;
    }
    if (e.button !== 0) return;

    if (this.#minimized) {
      // The whole stub is one button. It is not draggable — the desktop
      // tiles the strip — so there is nothing else a press on it could mean.
      this.restore();
      e.handled = true;
      return;
    }

    if (part === "title") {
      if (e.clickCount === 2 && this.canMaximize) {
        this.toggleMaximize();
        e.handled = true;
        return;
      }
      // A maximized window is dragged out of maximized only where snapping is on; elsewhere
      // it stays put, as it always has.
      if (this.#maximized && !this.desktop?.snapWindows) return;
      this.#beginDrag("move", part, e);
      e.handled = true;
      return;
    }
    if (part !== "client") {
      this.#beginDrag("resize", part, e);
      e.handled = true;
    }
  }

  /**
   * Start moving the window from a pointer event, as if its caption had been
   * grabbed.
   *
   * A borderless window has no caption, so whatever stands in for one — a
   * `Panel` docked across the top, a header `Label` — calls this from its own
   * `pointerdown` handler and marks the event handled. The same clamping and
   * capture apply as for a real caption drag.
   *
   * ```ts
   * header.on("pointerdown", (e) => {
   *   win.startMove(e);
   *   e.handled = true;
   * });
   * ```
   */
  startMove(e: MouseEventArgs): void {
    if (this.#maximized || this.#minimized || this.#drag) return;
    this.#beginDrag("move", "title", e);
  }

  #beginDrag(kind: "move" | "resize", part: WindowPart, e: MouseEventArgs): void {
    const unsnap = kind === "move" && (this.#maximized || this.#snapRestore !== null);
    if (kind === "resize") this.#snapRestore = null;
    this.#drag = { kind, part, origin: { ...e.screen }, startBounds: this.bounds, unsnap, zone: null };
    this.desktop?.input.setCapture(this);
  }

  /** Abandon a drag in flight, leaving the window wherever it got to. */
  #endDrag(): void {
    if (!this.#drag) return;
    this.#drag = null;
    const desktop = this.desktop;
    if (desktop) desktop.snapPreview = null;
    this.desktop?.input.releaseCapture(this);
  }

  #onPointerMove(e: MouseEventArgs): void {
    const drag = this.#drag;
    if (!drag) {
      const part = this.partAt({ x: e.x, y: e.y });
      const button =
        part === "minimize" || part === "maximize" || part === "close" ? part : null;
      const action = part === "action" ? this.captionActionAt({ x: e.x, y: e.y }) : null;
      if (button !== this.#hoveredButton || action !== this.#hoveredAction) {
        this.#hoveredButton = button;
        this.#hoveredAction = action;
        this.invalidate();
      }
      return;
    }

    const dx = e.screen.x - drag.origin.x;
    const dy = e.screen.y - drag.origin.y;
    const s = drag.startBounds;

    if (drag.kind === "move") {
      if (drag.unsnap) {
        if (Math.abs(dx) < UNSNAP_DISTANCE && Math.abs(dy) < UNSNAP_DISTANCE) return;
        this.#unsnap(drag, e.screen);
        this.#onPointerMove(e);
        return;
      }
      const at = this.#clampDrag({ x: s.x + dx, y: s.y + dy }, s);
      this.setLocation(at.x, at.y);
      this.#trackSnap(drag, e.screen);
      return;
    }

    let { x, y, width, height } = s;
    const part = drag.part;
    // A grip on the left or top edge moves the origin, so the size has to be limited here,
    // before the origin is worked out from it; setBounds alone would let the far edge slide.
    const lo = this.constrainSize(this.minWidth, this.minHeight);
    const hi = this.constrainSize(Infinity, Infinity);
    const minW = Math.max(this.minWidth, lo.width);
    const minH = Math.max(this.minHeight, lo.height);
    if (part.includes("w")) {
      width = clamp(s.width - dx, minW, Math.max(minW, hi.width));
      x = s.x + s.width - width;
    }
    if (part.includes("e")) width = clamp(s.width + dx, minW, Math.max(minW, hi.width));
    if (part.includes("n")) {
      height = clamp(s.height - dy, minH, Math.max(minH, hi.height));
      y = s.y + s.height - height;
    }
    if (part.includes("s")) height = clamp(s.height + dy, minH, Math.max(minH, hi.height));
    this.setBounds(x, y, width, height);
  }

  /**
   * Where a drag is allowed to put the window, under the desktop's clamp.
   *
   * `viewport` keeps the whole thing inside the work area. `reachable` leaves
   * a grabbable margin and lets the rest hang over. A window bigger than the
   * work area still slides freely under either, or it could not be dragged at
   * all — the clamp is there to keep it reachable, not to pin it.
   */
  #clampDrag(at: Point, size: { width: number; height: number }): Point {
    const desktop = this.desktop;
    if (!desktop || desktop.windowClamp === "off") return at;
    const area = desktop.workArea;
    // Spelt out rather than spread: `size` is the drag's start bounds, and
    // `{ ...at, ...size }` would put its x and y back over the dragged ones.
    const box = { x: at.x, y: at.y, width: size.width, height: size.height };
    // `slideRectInto` rather than `fitRectInto`: a drag must not resize what
    // it is moving, and a window bigger than the area still has to slide.
    return desktop.windowClamp === "viewport"
      ? slideRectInto(box, area)
      : nudgeRectIntoReach(box, area, DRAG_MARGIN, DRAG_MARGIN);
  }

  /**
   * Give a maximized or snapped window back its own size under the pointer, holding the point
   * of the caption that was grabbed in proportion, the way Windows does, and carry on the drag
   * from there.
   */
  #unsnap(drag: DragState, pointer: Point): void {
    const s = drag.startBounds;
    const fraction = s.width > 0 ? (drag.origin.x - s.x) / s.width : 0.5;
    const grabY = drag.origin.y - s.y;
    if (this.#maximized) this.restore();
    else if (this.#snapRestore) this.setSize(this.#snapRestore.width, this.#snapRestore.height);
    this.#snapRestore = null;
    this.setLocation(Math.round(pointer.x - fraction * this.width), Math.round(pointer.y - grabY));
    drag.unsnap = false;
    drag.origin = { ...pointer };
    drag.startBounds = this.bounds;
  }

  #trackSnap(drag: DragState, pointer: Point): void {
    const desktop = this.desktop;
    if (!desktop?.snapWindows || this.#borderStyle === "none") return;
    const area = desktop.workArea;
    let zone = snapZoneAt(pointer, area, desktop.snapEdge);
    if (zone === "top" && !this.canMaximize) zone = null;
    // A window that cannot be resized has no half or quarter to take.
    if (zone && zone !== "top" && !this.canResize) zone = null;
    drag.zone = zone;
    desktop.snapPreview = zone ? snapRect(zone, area) : null;
  }

  #applySnap(zone: SnapZone, from: Rect): void {
    const desktop = this.desktop;
    if (!desktop) return;
    if (zone === "top") {
      // Restoring from maximized goes back to where the drag began, not to the edge.
      this.setBounds(from.x, from.y, from.width, from.height);
      this.maximize();
      return;
    }
    this.#snapRestore ??= { width: from.width, height: from.height };
    const r = snapRect(zone, desktop.workArea);
    this.setBounds(r.x, r.y, r.width, r.height);
  }

  #onPointerUp(e: MouseEventArgs): void {
    const pressedAction = this.#pressedAction;
    if (pressedAction !== null) {
      this.#pressedAction = null;
      this.desktop?.input.releaseCapture(this);
      this.invalidate();
      if (this.captionActionAt({ x: e.x, y: e.y }) === pressedAction) {
        this.#captionActions[pressedAction]?.onClick(this);
      }
      return;
    }
    const pressed = this.#pressedButton;
    if (pressed) {
      this.#pressedButton = null;
      this.desktop?.input.releaseCapture(this);
      const part = this.partAt({ x: e.x, y: e.y });
      this.invalidate();
      if (part === pressed) {
        if (pressed === "close") this.close();
        else if (pressed === "maximize") this.toggleMaximize();
        else this.minimize();
      }
      return;
    }
    const drag = this.#drag;
    this.#endDrag();
    if (drag?.kind === "move" && drag.zone) this.#applySnap(drag.zone, drag.startBounds);
  }

  // --- keyboard ------------------------------------------------------------

  /** What the system menu offers, enabled as the window's state allows. */
  systemMenuItems(): MenuItemSpec[] {
    const sized = this.#maximized || this.#minimized;
    return [
      { text: "&Restore", enabled: sized, onClick: () => this.restore() },
      { text: "&Move", enabled: !sized && this.hasCaption, onClick: () => this.beginKeyboardTrack("move") },
      { text: "&Size", enabled: !this.#minimized && this.canResize, onClick: () => this.beginKeyboardTrack("size") },
      { text: "Mi&nimize", enabled: this.canMinimize && !this.#minimized, onClick: () => this.minimize() },
      { text: "Ma&ximize", enabled: this.canMaximize && !this.#maximized, onClick: () => this.maximize() },
      menuSeparator,
      { text: "&Close", shortcut: "Ctrl+F4", enabled: this.canClose, onClick: () => this.close() },
    ];
  }

  /** Open the system menu under the caption's left corner, as Alt+Space does on Windows. */
  showSystemMenu(): void {
    const desktop = this.desktop;
    if (!desktop || this.#borderStyle === "none") return;
    const at = this.#minimized ? this.#parkedRect() ?? this.bounds : this.bounds;
    const bar = this.titleBarRect;
    showMenu(desktop, this.systemMenuItems(), { x: at.x + bar.x, y: at.y + bar.y + bar.height });
  }

  /**
   * Move or size the window from the arrow keys, as the system menu's Move and Size do:
   * arrows step it, Ctrl+arrow by one unit, Enter keeps the result and Escape puts it back.
   * Size works the right and bottom edges.
   */
  beginKeyboardTrack(kind: "move" | "size"): void {
    if (this.#minimized || (kind === "move" && this.#maximized) || (kind === "size" && !this.canResize)) return;
    this.#keyTrack = { kind, start: this.bounds };
    this.activate();
  }

  /** True while a keyboard Move or Size is running. */
  get keyboardTracking(): boolean {
    return this.#keyTrack !== null;
  }

  /** Asked by the desktop before any control sees a key, while a keyboard Move or Size runs. */
  handleTrackingKey(args: KeyEventArgs): boolean {
    const track = this.#keyTrack;
    if (!track) return false;
    args.handled = true;
    if (args.key === "Enter") {
      this.#keyTrack = null;
      return true;
    }
    if (args.key === "Escape") {
      this.#keyTrack = null;
      const s = track.start;
      this.setBounds(s.x, s.y, s.width, s.height);
      return true;
    }
    const step = args.ctrlKey ? 1 : KEY_TRACK_STEP;
    const dx = args.key === "ArrowLeft" ? -step : args.key === "ArrowRight" ? step : 0;
    const dy = args.key === "ArrowUp" ? -step : args.key === "ArrowDown" ? step : 0;
    if (dx === 0 && dy === 0) return true;
    this.#snapRestore = null;
    if (track.kind === "move") {
      const at = this.#clampDrag({ x: this.x + dx, y: this.y + dy }, this.size);
      this.setLocation(at.x, at.y);
    } else {
      this.setSize(Math.max(this.minWidth, this.width + dx), Math.max(this.minHeight, this.height + dy));
    }
    return true;
  }

  /** Called by the Desktop after controls decline a key. Dialog overrides it. */
  handleDialogKey(_args: KeyEventArgs): boolean {
    return false;
  }

  // --- painting -----------------------------------------------------------

  /**
   * Draw the window, or — while it is parked — the stub standing in for it.
   *
   * The stub is drawn from the same theme calls as the window is, with a
   * stub-sized rectangle and a stub's visual state, so every theme gets one
   * for nothing and it always looks like the thing it came from.
   */
  override paint(rc: RenderContext, theme: Theme): void {
    this.#stepFlight();
    const flying = this.#flightRect();
    if (!flying && !this.#minimized) {
      super.paint(rc, theme);
      return;
    }
    // Minimized, not flying and not parked: something else — a taskbar — has
    // undertaken to show this window, and a stub as well would show it twice.
    const drawn = flying ?? this.#parkedRect();
    if (!drawn || !this.visible || drawn.width <= 0 || drawn.height <= 0) return;
    // The desktop painted us in at our own origin; this is somewhere else.
    rc.pushTransform(drawn.x - this.x, drawn.y - this.y, 1);
    this.#paintFrame(rc, theme, drawn);
    rc.popTransform();
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#paintFrame(rc, theme, this.bounds);
  }

  /** The frame and its title, at whatever rectangle the window is wearing. */
  #paintFrame(rc: RenderContext, theme: Theme, drawn: Rect): void {
    const state = this.windowState();
    if (state.borderStyle === "none") {
      theme.drawBorderlessFrame(rc, drawn, state);
      // No caption, so no caption text — the title is still carried for the
      // taskbar and the window list, it simply is not painted here.
      if (state.flash) theme.drawWindowFlash(rc, drawn, state);
      return;
    }
    theme.drawWindowFrame(rc, drawn, state);

    // The actions take room out of the title's rectangle, so a long title ellipsizes before it
    // runs under them rather than being drawn through.
    const actionRoom = state.minimized ? 0 : this.#captionActions.length * theme.metrics.captionButtonWidth;
    if (this.#title) {
      const titleRect = theme.titleTextRect(drawn, state);
      rc.drawTextInRect(
        this.#title,
        { ...titleRect, width: Math.max(0, titleRect.width - actionRoom) },
        theme.metrics.captionFont,
        state.active ? theme.palette.titleTextActive : theme.palette.titleTextInactive,
      );
    }
    if (!state.minimized) this.#paintCaptionActions(rc, theme, state);
    // Over the finished caption, text included, so the whole bar pulses.
    if (state.flash) theme.drawWindowFlash(rc, drawn, state);
  }

  /**
   * The application's caption buttons, drawn as the theme's own are: a hover or press wash in the
   * theme's control colors, and the glyph in the caption's ink.
   */
  #paintCaptionActions(rc: RenderContext, theme: Theme, state: WindowVisualState): void {
    const p = theme.palette;
    this.#captionActions.forEach((action, index) => {
      const r = this.captionActionRect(index);
      if (!r) return;
      const hovered = this.#hoveredAction === index;
      const pressed = this.#pressedAction === index;
      if (hovered || pressed) rc.fillRect(r, pressed ? p.controlBgPressed : p.controlBgHover);
      const paint = typeof action.glyph === "string" ? createGlyph(action.glyph) : action.glyph;
      if (!paint) return;
      const size = Math.max(8, Math.min(r.width, r.height) - 14);
      const box: Rect = { x: r.x + (r.width - size) / 2, y: r.y + (r.height - size) / 2, width: size, height: size };
      paint({ rc, box, ink: state.active ? p.titleTextActive : p.titleTextInactive, weight: Math.max(1, Math.round(size / 7)) });
    });
  }

  /** Windows never hand a hit through to the desktop behind them. */
  protected override hitTestSelf(_p: Point): boolean {
    return true;
  }

  override contains(other: Control | null): boolean {
    return super.contains(other);
  }

  /** Its chrome — the title bar and the eight resize grips — but not inside. */
  override handlesDragAt(p: Point): boolean {
    return this.partAt(p) !== "client";
  }
}
